import { ROOMTYPES } from 'frontend-utils/models/roomtype';
import {
  OTA_IDS,
  PLATFORMS,
} from 'frontend-utils/utils/constants/constant-values';
import type { ReferencePeriod } from 'lumen/controllers/market/total-revenue';
import { EVENTS_CATEGORIES, EVENTS_STATUSES } from 'lumen/models/event';
import type { MarketTotalRevenueParams } from 'lumen/routes/market/total-revenue';
import {
  DATE_AGGREGATION,
  MEALTYPES,
  RATE_PARAMS,
} from 'lumen/utils/constants/general-constants';
import { SEARCHES_TABS } from 'lumen/utils/constants/market-insight-constants';
import {
  DATE_AGGREGATION_WEEK,
  DEFAULT_LOS,
  DEFAULT_POS,
  PARITY_FILTER,
  RATE_TYPES,
  SORT_TYPES,
} from 'lumen/utils/constants/pi-constant-values';
import { PA_REF_DAYS_AGO } from 'lumen/utils/constants/pricing-assistant-constants';
import { RATES_VIEWS } from 'lumen/utils/constants/rate-constants';
import {
  DETAIL_VIEW_TABS,
  REFERENCE_PERIODS,
  SUBTYPE_VALUES_KEY_CANCELLATION_POLICY,
  TYPE_ID_BEST_FLEX,
} from 'lumen/utils/constants/rate-strategy-constants';
import {
  CANCELLATIONS_VIEWS,
  KPI_CATEGORIES,
  KPIS,
} from 'lumen/utils/constants/ri-constant-values';
import { AL_REF_DAYS_AGO_VALUES } from 'lumen/utils/constants/short-term-rental-constants';
import {
  METRICS as TOTAL_REVENUE_METRICS,
  VIEWS as TOTAL_REVENUE_VIEWS,
} from 'lumen/utils/constants/total-revenue-constants';

/**
 * Every query parameter that has refreshModel set to false should have a default value specified below. This is required
 * for correct usage tracking. For more info on why this is needed check queryParamsDidChange in tracking-route.js
 *
 * Query parameters should be nested in the structure of the route they're defined in. For example, the QP 'OTAGroup' in
 * the hotel.parity.list route should be written as follows: hotel: { parity: { list: { OTAGroup: defaultvalue... } } };
 */
export const DEFAULT_QP_VALUES = {
  hotel: {
    'short-term-rental': {
      refDaysAgo: AL_REF_DAYS_AGO_VALUES.yesterday,
      dateAggregation: DATE_AGGREGATION.DAY,
    },
    overview: {
      refDaysAgo: 1,
      activeFilters: '',
    },
    'pricing-manager': {
      los: 1, // Preferred rate settings currently don't have any LOS value. Therefore, we currently hardcode LOS 1;
      platform: PLATFORMS.PLATFORM_UNSPECIFIED, // Currently we use the rates mode default value
      compsetId: '1', // Currently we only support primary compset in pricing mode
      membershipType: 0,
      refDaysAgo: PA_REF_DAYS_AGO,
      detail: {
        edit: false,
      },
      'pricing-manager-dynamic-rate': {
        platform: PLATFORMS.PLATFORM_DESKTOP, // For DRP, we only suport desktop platform for now
      },
    },
    rates: {
      view: RATES_VIEWS.CALENDAR,
      los: 0,
      mealPlan: 0,
      maxPersons: 0,
      membershipType: 0,
      roomType: ROOMTYPES.ALL,
      rateParam: RATE_PARAMS.BEST_FLEX,
      otaId: OTA_IDS.BOOKINGDOTCOM,
      compareType: '',
      compareValue: '',
      compsetId: '',
      platform: PLATFORMS.PLATFORM_UNSPECIFIED,
    },
    events: {
      eventsStatuses: [EVENTS_STATUSES.ACCEPTED, EVENTS_STATUSES.SUGGESTED],
      eventsCategories: Object.values(EVENTS_CATEGORIES),
    },
    parity: {
      list: {
        OTAGroup: 'major',
        rateParam: RATE_PARAMS.LOWEST,
        mealPlan: 0,
        membershipType: 0,
        roomType: ROOMTYPES.ALL,
        los: 0,
        pos: DEFAULT_POS,
        maxPersons: 0,
        OTAId: 'allchannels',
        platform: PLATFORMS.PLATFORM_UNSPECIFIED,
      },
    },
    'revenue-insight': {
      scheduleReport: false,
      selectedView: 'net',
      stayDateAggregation: 'day',
      kpi: KPIS.OCCUPANCY,
      kpiCategory: KPI_CATEGORIES.TOTAL,
      roomsTab: KPIS.OCCUPANCY,
      revenueTab: KPIS.REVENUE,
      cancellationsView: CANCELLATIONS_VIEWS.ABSOLUTE,
    },
    'market-insight': {
      selectedCalendarViewFilterIds: [],
      focusedDetailSection: '',
      extractDateIndex: 0,
      showAllEvents: false,
      evolutionType: '',
      searchesTab: SEARCHES_TABS.FLIGHTS,
      searchesDetailsItem: SEARCHES_TABS.FLIGHTS,
      isSearchesCardExpanded: true,
      refDaysAgo: 7,
      scrollTo: null,
    },
    settings: {
      mapping: {
        sortBy: 'category',
        sortDirection: 'desc',
        page: 1,
      },
    },
    strategy: {
      sortBy: 'hotelinfo',
      sortDirection: 'asc',
      tab: DETAIL_VIEW_TABS.TABLE,
      referencePeriod: REFERENCE_PERIODS.ONE_MONTH_AGO,
      cancellation: {
        sortBy: 'hotelinfo',
        sortDirection: 'asc',
        entity: SUBTYPE_VALUES_KEY_CANCELLATION_POLICY,
        subtype: TYPE_ID_BEST_FLEX,
      },
      position: {
        sortBy: 'hotelinfo',
        sortDirection: 'asc',
      },
      dow: {
        sortBy: 'hotelinfo',
        sortDirection: 'asc',
        tab: DETAIL_VIEW_TABS.GRAPH,
      },
      leadtime: {
        sortBy: 'hotelinfo',
        sortDirection: 'asc',
        tab: DETAIL_VIEW_TABS.GRAPH,
      },
      los: {
        sortBy: 'hotelinfo',
        sortDirection: 'asc',
        subtype: '',
      },
      mealtype: {
        sortBy: 'hotelinfo',
        sortDirection: 'asc',
        subtype: '',
      },
      mobile: {
        sortBy: 'hotelinfo',
        sortDirection: 'asc',
        subtype: '',
      },
      pos: {
        sortBy: 'hotelinfo',
        sortDirection: 'asc',
        subtype: '',
      },
      membership: {
        sortBy: 'hotelinfo',
        sortDirection: 'asc',
        subtype: '',
      },
      roomtype: {
        sortBy: 'hotelinfo',
        sortDirection: 'asc',
        roomtype: 'roomtype_standard',
        tab: DETAIL_VIEW_TABS.GRAPH,
      },
    },
  },
  application: {
    compsetId: '1',
    hidePickupLY: false,
    los: 0,
    maxPersons: 0,
    mealPlan: 0,
    membershipType: 0,
    otaId: OTA_IDS.BOOKINGDOTCOM,
    platform: PLATFORMS.PLATFORM_UNSPECIFIED,
    rateParam: RATE_PARAMS.BEST_FLEX,
    roomType: 'all',
    reference: 'SWLY',
    scheduleReport: undefined, // TODO: can be cleaned up?
  },
  market: {
    marketrates: {
      view: 'table',
    },
    parity: {
      insight: {
        extractDate: '',
        historicalExtractDate: '',
        benchmark: true,
        dateAggregation: DATE_AGGREGATION_WEEK,
        sortType: SORT_TYPES.ABSOLUTE,
        sortKey: PARITY_FILTER.LOSS,
        segmentationFilters: '',
        rateType: RATE_TYPES.LOWEST,
        los: DEFAULT_LOS,
        pos: DEFAULT_POS,
        mealPlan: MEALTYPES.ANY,
        trends: {
          selection: PARITY_FILTER.LOSS,
        },
        historical: {
          platform: PLATFORMS.PLATFORM_DESKTOP,
        },
      },
      strategy: {
        extractDate: '',
        historicalExtractDate: '',
      },
      'parity-manager': {
        selectedIssueSummaryId: null,
      },
    },
    'revenue-insight': {
      stayDateAggregation: 'day',
      kpi: KPIS.OCCUPANCY,
      kpiCategory: KPI_CATEGORIES.TOTAL,
      roomsTab: KPIS.OCCUPANCY,
      revenueTab: KPIS.REVENUE,
    },
    'total-revenue': {
      view: TOTAL_REVENUE_VIEWS.DEPARTMENTS as MarketTotalRevenueParams['view'],
      referencePeriods: ['STLY'] as [ReferencePeriod, ...ReferencePeriod[]],
      portfolios: [] as MarketTotalRevenueParams['portfolios'],
      properties: [] as MarketTotalRevenueParams['properties'],
      focusedProperties: [] as MarketTotalRevenueParams['focusedProperties'],
      isPerAvailableRoom:
        false as MarketTotalRevenueParams['isPerAvailableRoom'],
      trendMetric:
        TOTAL_REVENUE_METRICS.TOTAL_REVENUE as MarketTotalRevenueParams['trendMetric'],
      trendAggregation: 'day' as MarketTotalRevenueParams['trendAggregation'],
    },
  },
} as const;
